/**
 * The career, as something that survives a reload.
 *
 * Everything built between steps 49 and 52 — the ladder, the record, the
 * head-to-head ledger, the climb towards the card — was wiped by a refresh.
 * A ladder you can never actually climb across a session is a ladder that
 * exists but does not *happen*, and this is the smallest change that makes all
 * of it matter.
 *
 * Two rules shape the module.
 *
 * **The save owns state, not identity.** Names, styles, tiers and stat spreads
 * are not in here: they come out of `ladder.json`, and a save that carried its
 * own copy would quietly keep fighting last week's roster after the file
 * changed. What a save owns is the order, the records, the ledger and the
 * counters — the things a career *did*. Who these people are is the file's.
 *
 * **It is all or nothing.** A payload is checked whole before anything is
 * touched, so a save from an older shape is discarded with a reason rather than
 * applied down to the field where it stops making sense. Half a career is worse
 * than no career: no career is a fresh start, and half of one is a bug nobody
 * can describe.
 *
 * Pure. `saveStore.js` is the half that touches the browser.
 */

/**
 * The shape number. **Bump it whenever what `snapshot` writes changes**, and a
 * save written by the old shape is refused rather than guessed at.
 *
 * 1 — ladder order + records, campaign counters, history and ledger, the
 *     player's styles and heat.
 * 2 — draws: a third outcome on every record, in the ledger and in the
 *     campaign's own counters. A version 1 save has no `draws` anywhere and
 *     loading it would read every one of them as zero, which is a career that
 *     quietly disagrees with the fights it had.
 * 3 — `ducks`: how many reach cards have gone by in a row. A version 2 save has
 *     none, and loading it as zero would hand somebody who has ducked four
 *     fights a clean slate with the crowd — which is the one thing that
 *     mechanic is for.
 * 4 — the bank: cash, development points, and what both have been spent on. A
 *     version 3 save has no money in it at all, and loading it would put a
 *     fighter in the ring wearing gear they had not bought and could not have
 *     bought — the spread comes off the bank now.
 * 5 — the master volume and mute. Not a thing a career *did*, and the one
 *     entry in here that is a preference rather than a record — but this game
 *     has exactly one slot, and a mute that did not survive a reload would be
 *     a mute nobody could rely on.
 * 6 — the player's **name**. The create screen (step 67) means a fighter is
 *     somebody a person invented, and there is no entry in `ladder.json` to
 *     fall back to any more — "You" was a placeholder for a career nobody had
 *     named yet. It is the one identity a save owns, and gotcha 60 now says so.
 * 7 — the venues: who holds each of the four rooms (`ladder.venues`). A
 *     version 6 save has none, and loading it would hand every room back to
 *     the file's holders — quietly taking a room off a player who had won it,
 *     which is the one thing holding a room is supposed to protect against.
 * 8 — the rooms' money: every fighter's ledger (purses and rooms) and every
 *     tab not yet collected. A version 7 save has neither, and loading it
 *     would quietly drop money the player had earned in their rooms and not
 *     yet fought to pick up.
 * 9 — the wardrobe: gear is six slots of tiers (step 93), so the bank also
 *     carries what is **worn** in each slot and the looks of the last few
 *     fights, which is how the crowd's boredom survives a reload. A version 8
 *     save has neither, and its gear ids are mostly from a shop that no longer
 *     exists.
 */
export const SAVE_VERSION = 9;

/** What a save must carry. Anything missing is a save from a different game. */
const REQUIRED = Object.freeze(['version', 'ladder', 'campaign', 'player', 'bank', 'audio']);

/** Everything a career is, as plain data. */
export function snapshotCareer({ ladder, campaign, player = null, bank = null, audio = null }) {
  return {
    version: SAVE_VERSION,
    at: new Date().toISOString(),
    ladder: ladder.snapshot(),
    campaign: campaign.snapshot(),
    /**
     * What the career earned and what it went on. `null` rather than absent for
     * a career with no bank behind it, so the key is always there and
     * `REQUIRED` means what it says.
     */
    bank: bank?.snapshot?.() ?? null,
    /** The master volume and mute. `null` for a game with no audio behind it. */
    audio: audio?.snapshot?.() ?? null,
    player: {
      /**
       * A name a person typed, which no file can answer for. Read off the
       * *roster entry* rather than off the combatant, because the entry is what
       * `bookFighter` dresses a fighter from and what every screen reads.
       */
      name: ladder.player?.name ?? null,
      // The player's styles are theirs rather than the roster's, so they are
      // saved with the name for the same reason.
      styles: [...(player?.loadout?.ids ?? [])],
      heat: player?.heat?.snapshot?.() ?? null,
    },
  };
}

/**
 * Whether a payload can be loaded, and if not, why in words.
 *
 * The reason is a sentence rather than a code because it is shown to somebody:
 * a career that vanished deserves an explanation, and "save is from version 1,
 * this game reads 2" is one. Silence is not.
 */
export function inspectSave(data) {
  if (data === null || data === undefined) {
    return { ok: false, reason: 'no career saved yet' };
  }
  if (typeof data !== 'object' || Array.isArray(data)) {
    return { ok: false, reason: 'the saved career is not an object' };
  }
  // The version first, and before the other keys are counted. A save from
  // another shape is *expected* to be missing things this one requires, and
  // "there is no bank" is a worse answer than "that is a version 3 save" when
  // the reason there is no bank is that version 3 had none.
  if (data.version === undefined) {
    return { ok: false, reason: 'the saved career has no version' };
  }
  if (data.version !== SAVE_VERSION) {
    return {
      ok: false,
      outdated: true,
      reason: `the saved career is version ${JSON.stringify(data.version)}; this game reads ${SAVE_VERSION}`,
    };
  }
  for (const key of REQUIRED) {
    if (data[key] === undefined) {
      return { ok: false, reason: `the saved career has no ${key}` };
    }
  }
  if (!Array.isArray(data.ladder?.order) || data.ladder.order.length === 0) {
    return { ok: false, reason: 'the saved career has no ladder order' };
  }
  if (typeof data.campaign !== 'object' || data.campaign === null) {
    return { ok: false, reason: 'the saved career has no campaign' };
  }
  return { ok: true, reason: null, at: data.at ?? null };
}

/**
 * A saved career in one line, without loading it.
 *
 * The title screen (step 66) offers *continue* and has to say what it is
 * continuing — a button that reads only "continue" asks somebody to remember a
 * career they last touched a week ago. Derived from the payload rather than
 * from a loaded game, because the whole point is that nothing has been loaded
 * yet and a title screen that half-applied a save to describe it would be the
 * thing `restoreCareer` exists to prevent.
 *
 * `ok` is `inspectSave`'s answer. Everything else is `null` when it is false:
 * there is no honest summary of a payload this version cannot read.
 */
export function describeSave(data, { playerId = 'player' } = {}) {
  const check = inspectSave(data);
  if (!check.ok) return Object.freeze({ ...check, name: null, rank: null, fights: null });
  const order = data.ladder?.order ?? [];
  const at = order.indexOf(playerId);
  const campaign = data.campaign ?? {};
  return Object.freeze({
    ...check,
    name: data.player?.name ?? null,
    rank: at < 0 ? null : at + 1,
    of: order.length || null,
    fights: Number(campaign.fights) || 0,
    wins: Number(campaign.wins) || 0,
    losses: Number(campaign.losses) || 0,
    draws: Number(campaign.draws) || 0,
    cash: Number(data.bank?.cash) || 0,
  });
}

/**
 * Puts a career back, or refuses it whole.
 *
 * The ladder goes first and is allowed to veto: it is the only part that can
 * tell a save from *this* roster from a save from a different one, because it
 * is the only part that names fighters. If it refuses, nothing else is touched
 * — which is the difference between discarding a save and half-applying one.
 */
export function restoreCareer(data, { ladder, campaign, player = null, bank = null, audio = null }) {
  const check = inspectSave(data);
  if (!check.ok) return check;

  if (!ladder.restore(data.ladder)) {
    return { ok: false, reason: 'the saved career is for a different roster' };
  }
  campaign.restore(data.campaign);
  // After the ladder has had its veto, like everything else: a bank restored
  // ahead of a refused roster would be money in a career that was thrown away.
  if (bank && data.bank) bank.restore(data.bank);
  // After the veto too, though nothing about a volume belongs to a roster — it
  // rides in this payload because this payload is the only slot there is, and a
  // discarded save should not leave half its settings applied either.
  if (audio && data.audio) audio.restore(data.audio);

  // The name and the styles go back onto the **roster entry**, which is what
  // dresses a fighter and what every screen reads. Doing it to the combatant
  // instead would put the right name in the ring and "You" on every card.
  if (data.player?.name || Array.isArray(data.player?.styles)) {
    try {
      ladder.identify(ladder.playerId, {
        name: data.player.name,
        styles: data.player.styles,
      });
    } catch {
      // A style that no longer exists. The roster's answer stands.
    }
  }

  if (player) {
    const styles = data.player?.styles;
    if (Array.isArray(styles) && styles.length) {
      // Guarded: a style that no longer exists throws out of `createLoadout`,
      // and a career should not be unloadable because a style was renamed.
      try {
        player.setStyles(styles);
      } catch {
        // Left on whatever the roster dressed them in.
      }
    }
    if (data.player?.heat) player.heat?.restore?.(data.player.heat);
  }

  return { ok: true, reason: null, at: data.at ?? null };
}
